/**
 * Tasks.md Parser - Markdown ↔ Task 변환 유틸리티
 *
 * Tasks.md 포맷:
 * ```markdown
 * # Tasks
 *
 * ## Backlog (todo)
 * - [ ] [HIGH] 태스크 제목 @assignee #due:2024-02-15
 *   > 태스크 설명 (선택)
 *
 * ## In Progress (in_progress)
 * - [ ] [URGENT] 진행 중인 태스크
 *
 * ## In Review (in_review)
 * - [ ] 검토 중인 태스크
 *
 * ## Done (done)
 * - [x] 완료된 태스크
 * ```
 */

import type { Task, TaskStatus, TaskPriority, TaskBoardData } from '@/types';

// Section headers and their corresponding status
const SECTION_STATUS_MAP: Record<string, TaskStatus> = {
  'backlog': 'todo',
  'to do': 'todo',
  'todo': 'todo',
  'in progress': 'in_progress',
  'in_progress': 'in_progress',
  'in review': 'in_review',
  'in_review': 'in_review',
  'done': 'done',
  'completed': 'done',
  'cancelled': 'cancelled',
};

const STATUS_SECTION_MAP: Record<TaskStatus, string> = {
  'todo': 'Backlog',
  'in_progress': 'In Progress',
  'in_review': 'In Review',
  'done': 'Done',
  'cancelled': 'Cancelled',
};

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  'low': 'LOW',
  'medium': 'MEDIUM',
  'high': 'HIGH',
  'urgent': 'URGENT',
};

const LABEL_PRIORITY_MAP: Record<string, TaskPriority> = {
  'LOW': 'low',
  'MEDIUM': 'medium',
  'HIGH': 'high',
  'URGENT': 'urgent',
};

interface ParsedTask {
  id?: string;
  title: string;
  description?: string;
  priority: TaskPriority;
  status: TaskStatus;
  assignee?: string;
  dueDate?: string;
  labels: string[];
  completed: boolean;
}

/**
 * Parse a single task line from Markdown
 * Format: - [ ] [PRIORITY] Task title @assignee #due:YYYY-MM-DD #label1 #label2
 */
function parseTaskLine(line: string, currentStatus: TaskStatus): ParsedTask | null {
  // Match checkbox pattern
  const checkboxMatch = line.match(/^[-*]\s*\[([ xX])\]\s*/);
  if (!checkboxMatch) return null;

  const completed = checkboxMatch[1].toLowerCase() === 'x';
  let content = line.slice(checkboxMatch[0].length);

  // Extract priority [HIGH], [LOW], etc.
  let priority: TaskPriority = 'medium';
  const priorityMatch = content.match(/^\[(\w+)\]\s*/);
  if (priorityMatch && LABEL_PRIORITY_MAP[priorityMatch[1].toUpperCase()]) {
    priority = LABEL_PRIORITY_MAP[priorityMatch[1].toUpperCase()];
    content = content.slice(priorityMatch[0].length);
  }

  // Extract assignee @username
  let assignee: string | undefined;
  const assigneeMatch = content.match(/@(\w+)/);
  if (assigneeMatch) {
    assignee = assigneeMatch[1];
    content = content.replace(/@\w+/, '').trim();
  }

  // Extract due date #due:YYYY-MM-DD
  let dueDate: string | undefined;
  const dueDateMatch = content.match(/#due:(\d{4}-\d{2}-\d{2})/);
  if (dueDateMatch) {
    dueDate = dueDateMatch[1];
    content = content.replace(/#due:\d{4}-\d{2}-\d{2}/, '').trim();
  }

  // Extract labels #label
  const labels: string[] = [];
  const labelMatches = content.matchAll(/#(\w+)/g);
  for (const match of labelMatches) {
    if (match[1] !== 'due') {
      labels.push(match[1]);
    }
  }
  content = content.replace(/#\w+/g, '').trim();

  // Remaining content is the title
  const title = content.trim();
  if (!title) return null;

  return {
    title,
    priority,
    status: currentStatus,
    assignee,
    dueDate,
    labels,
    completed,
  };
}

/**
 * Parse description block (lines starting with >)
 */
function parseDescription(lines: string[], startIndex: number): { description: string; endIndex: number } {
  const descLines: string[] = [];
  let i = startIndex;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (line.startsWith('>')) {
      descLines.push(line.slice(1).trim());
      i++;
    } else if (line === '' && descLines.length > 0) {
      // Allow empty lines within description
      i++;
      continue;
    } else {
      break;
    }
  }

  return {
    description: descLines.join('\n'),
    endIndex: i - 1,
  };
}

/**
 * Parse Tasks.md content into structured task data
 */
export function parseTasksMd(content: string): ParsedTask[] {
  const lines = content.split('\n');
  const tasks: ParsedTask[] = [];
  let currentStatus: TaskStatus = 'todo';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Check for section header (## Section Name or ## Section Name (status))
    const sectionMatch = trimmedLine.match(/^##\s+(.+?)(?:\s*\((\w+)\))?$/);
    if (sectionMatch) {
      const sectionName = sectionMatch[1].toLowerCase();
      const explicitStatus = sectionMatch[2]?.toLowerCase();

      if (explicitStatus && SECTION_STATUS_MAP[explicitStatus]) {
        currentStatus = SECTION_STATUS_MAP[explicitStatus];
      } else if (SECTION_STATUS_MAP[sectionName]) {
        currentStatus = SECTION_STATUS_MAP[sectionName];
      }
      continue;
    }

    // Parse task line
    const task = parseTaskLine(trimmedLine, currentStatus);
    if (task) {
      // Check for description on next lines
      if (i + 1 < lines.length) {
        const { description, endIndex } = parseDescription(lines, i + 1);
        if (description) {
          task.description = description;
          i = endIndex;
        }
      }

      // Override status if completed checkbox
      if (task.completed && task.status !== 'done') {
        task.status = 'done';
      }

      tasks.push(task);
    }
  }

  return tasks;
}

/**
 * Convert tasks to TaskBoardData structure
 */
export function tasksToBoard(tasks: ParsedTask[]): TaskBoardData {
  const board: TaskBoardData = {
    todo: [],
    in_progress: [],
    in_review: [],
    done: [],
  };

  tasks.forEach((task, index) => {
    const fullTask: Task = {
      id: task.id || `temp-${index}`,
      projectId: '',
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      assigneeName: task.assignee,
      dueDate: task.dueDate,
      labels: task.labels,
      position: index,
      metadata: {},
      isOverdue: false,
      isDueSoon: false,
      canComplete: task.status !== 'done',
      canReopen: task.status === 'done',
      hasSourceArtifact: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Calculate overdue status
    if (task.dueDate) {
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      fullTask.isOverdue = dueDate < today && task.status !== 'done';
      const threeDaysFromNow = new Date(today);
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
      fullTask.isDueSoon = dueDate <= threeDaysFromNow && !fullTask.isOverdue;
    }

    if (task.status !== 'cancelled') {
      board[task.status].push(fullTask);
    }
  });

  return board;
}

/**
 * Convert a single task to Markdown line format
 */
function taskToMdLine(task: Task): string {
  const parts: string[] = [];

  // Checkbox
  const checkbox = task.status === 'done' ? '[x]' : '[ ]';
  parts.push(`- ${checkbox}`);

  // Priority (only if not medium)
  if (task.priority !== 'medium') {
    parts.push(`[${PRIORITY_LABELS[task.priority]}]`);
  }

  // Title
  parts.push(task.title);

  // Assignee
  if (task.assigneeName) {
    parts.push(`@${task.assigneeName.replace(/\s+/g, '_')}`);
  }

  // Due date
  if (task.dueDate) {
    parts.push(`#due:${task.dueDate}`);
  }

  // Labels
  task.labels.forEach((label) => {
    parts.push(`#${label}`);
  });

  return parts.join(' ');
}

/**
 * Convert TaskBoardData to Tasks.md content
 */
export function boardToTasksMd(board: TaskBoardData): string {
  const sections: string[] = ['# Tasks', ''];

  const statusOrder: TaskStatus[] = ['todo', 'in_progress', 'in_review', 'done'];

  statusOrder.forEach((status) => {
    const tasks = board[status];
    if (!tasks || tasks.length === 0) {
      // Include empty sections for consistency
      sections.push(`## ${STATUS_SECTION_MAP[status]} (${status})`);
      sections.push('');
      return;
    }

    sections.push(`## ${STATUS_SECTION_MAP[status]} (${status})`);

    tasks.forEach((task) => {
      sections.push(taskToMdLine(task));
      if (task.description) {
        task.description.split('\n').forEach((line) => {
          sections.push(`  > ${line}`);
        });
      }
    });

    sections.push('');
  });

  return sections.join('\n');
}

/**
 * Merge changes from Tasks.md into existing tasks
 * - Preserves IDs for existing tasks (matched by title)
 * - Marks new tasks for creation
 * - Marks removed tasks for deletion
 */
export function mergeTaskChanges(
  existingTasks: Task[],
  parsedTasks: ParsedTask[]
): {
  toCreate: ParsedTask[];
  toUpdate: { task: Task; changes: Partial<Task> }[];
  toDelete: Task[];
} {
  const result = {
    toCreate: [] as ParsedTask[],
    toUpdate: [] as { task: Task; changes: Partial<Task> }[],
    toDelete: [] as Task[],
  };

  const existingByTitle = new Map<string, Task>();
  existingTasks.forEach((task) => {
    existingByTitle.set(task.title.toLowerCase(), task);
  });

  const processedTitles = new Set<string>();

  parsedTasks.forEach((parsed) => {
    const titleKey = parsed.title.toLowerCase();
    processedTitles.add(titleKey);

    const existing = existingByTitle.get(titleKey);
    if (existing) {
      // Check for changes
      const changes: Partial<Task> = {};

      if (existing.status !== parsed.status) {
        changes.status = parsed.status;
      }
      if (existing.priority !== parsed.priority) {
        changes.priority = parsed.priority;
      }
      if (existing.description !== parsed.description) {
        changes.description = parsed.description || '';
      }
      if (existing.dueDate !== parsed.dueDate) {
        changes.dueDate = parsed.dueDate;
      }
      if (JSON.stringify(existing.labels) !== JSON.stringify(parsed.labels)) {
        changes.labels = parsed.labels;
      }

      if (Object.keys(changes).length > 0) {
        result.toUpdate.push({ task: existing, changes });
      }
    } else {
      result.toCreate.push(parsed);
    }
  });

  // Find deleted tasks
  existingTasks.forEach((task) => {
    if (!processedTitles.has(task.title.toLowerCase())) {
      result.toDelete.push(task);
    }
  });

  return result;
}

/**
 * Generate a default Tasks.md template
 */
export function generateDefaultTasksMd(): string {
  return `# Tasks

## Backlog (todo)

_새 태스크를 추가하세요_

## In Progress (in_progress)

_진행 중인 태스크가 없습니다_

## In Review (in_review)

_검토 중인 태스크가 없습니다_

## Done (done)

_완료된 태스크가 없습니다_
`;
}

export default {
  parseTasksMd,
  tasksToBoard,
  boardToTasksMd,
  mergeTaskChanges,
  generateDefaultTasksMd,
};
